/**
 * Phase 336 wave 3 task 3.2 (#1163) — the client's Vacancies page, to the design Alisha
 * approved.
 *
 * <h3>What this file is, and what it deliberately is not</h3>
 * <p>It is a RENDERER, exactly like its sibling {@link ./SalesActivityPage}. Every number and
 * every rule about what a number MEANS comes from `../reports/vacanciesModel.js`: which roles
 * belong to which month, which roles are overdue and therefore belong to no month at all, what
 * the A+R headline sums, what the "incl. B" split adds, which consultant wears which
 * categorical hue, and how a £ figure is written. Nothing is re-derived here — a second copy of
 * "who owns this vacancy" or "which month is this in" is precisely how the Teams card and the
 * portal end up telling one client two different stories about the same week.</p>
 *
 * <p>What IS this file's own: the composition (headline → chart → overdue → month bands), the
 * four states a read surface must have, and the presentation of the overdue partition.</p>
 *
 * <h3>The one rule this page exists to keep</h3>
 * <p><b>An overdue vacancy is never shown under a month band.</b> The wire flags it and keeps
 * its real, PAST expected month (`ForecastRoleRowDto.overdue` — "a consumer MUST branch on this
 * flag and MUST NOT month-group an overdue role into a forecast month"), so the model splits
 * the payload into {@link monthGroups} and {@link overdueRoles} and this page renders them as
 * two separate tables. Re-keying an overdue role to a calendar band is what put year-old rows
 * in a live forecast on card #1072; two tables makes that structurally impossible rather than
 * merely discouraged.</p>
 *
 * <h3>Why overdue comes BEFORE the months</h3>
 * <p>Because the family already decided it: the Monday forecast card writes its `overdue`
 * bucket ahead of `upcoming_months` (`ForecastDeliveryService.BuildForecastPayload`) on the
 * grounds that overdue is the attention item — "its job is attention … not the headline". The
 * portal keeps that order so a client reading the card and then the page meets the same
 * information in the same sequence. And, like the card, the section is OMITTED entirely when
 * nothing is overdue rather than rendering an alarming empty "Overdue" heading.</p>
 *
 * <h3>Why a month with no vacancies simply is not there</h3>
 * <p>Bands come from the roles, so an empty month has no band and nothing suppresses it. That
 * is the opposite of the Sales Activity table, where a MISSING week between two recorded weeks
 * is a labelled gap — and deliberately so: a week nobody recorded is a measurement that went
 * missing, while a month nobody expects to place in is simply a quiet month. Inventing a "no
 * vacancies" band for it would be zero-noise on a forecast.</p>
 *
 * <h3>Freshness</h3>
 * <p>The "Data as at" line is the payload's own `as_of` — the instant the forecast was
 * computed, and the same instant its `overdue` flags were decided against (ADR-068 D3 /
 * ADR-074 D4). Never the moment this browser fetched it.</p>
 *
 * <p>Design tokens only — no raw hex (test/polish/hex-fence.test.ts).</p>
 */
import { Fragment } from "react";
import { EmptyState, PageHeader, PriorityChip, StackedBarChart } from "@ardept/design-system";
import { useClientTenant, useForecastRoles } from "../api/portalQueries.js";
import { VACANCIES_LABEL } from "../nav/familyRegistry.js";
import { usePageTitle } from "../nav/usePageTitle.js";
import {
  OVERDUE_LABEL,
  formatGbp,
  headline,
  monthGroups,
  monthLabel,
  overdueRoles,
  stackedChartData,
  type VacancyRow,
} from "../reports/vacanciesModel.js";
import "./VacanciesPage.css";

// ── Vocabulary this PAGE owns (the model owns the rest) ─────────────────────────────────

/** The role table's columns, in presentation order. */
const ROLE_COLUMNS = ["Vacancy", "Consultant", "Priority", "Expected value"] as const;

/** The caption over the headline figure when the tenant maintains no priorities. */
const ALL_VACANCIES_CAPTION = "All vacancies";

/** What a role row with no priority letter shows in the Priority column. */
const NO_PRIORITY_MARK = "–";

// ── Small helpers ───────────────────────────────────────────────────────────────────────

/** A count of vacancies in the client's words: `1 vacancy`, `3 vacancies`. */
function vacancyCount(count: number): string {
  return count === 1 ? "1 vacancy" : `${count} vacancies`;
}

/** The compute's `as_of`, as a date a person reads rather than an ISO timestamp. */
function formatAsOf(asOf: string | null | undefined): string {
  if (!asOf) return "";
  const when = new Date(asOf);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

/** The shared column head — one definition, so the two role tables cannot drift apart. */
function RoleColumns(): JSX.Element {
  return (
    <thead>
      <tr>
        {ROLE_COLUMNS.map((column) => (
          <th
            scope="col"
            key={column}
            className={column === "Expected value" ? "num" : undefined}
          >
            {column}
          </th>
        ))}
      </tr>
    </thead>
  );
}

/**
 * One vacancy, in either table.
 *
 * <p>The consultant's swatch is the SAME `var(--ard-cat-N)` their band in the chart is painted
 * with (both come from one identity map in the model), which is what lets a reader look from a
 * column to a name without a second legend. It is `aria-hidden`: the name beside it already
 * carries the identity, and a screen reader announcing a colour would be noise.</p>
 *
 * <p>The overdue flag is INLINE, on the row, naming the month that has been and gone. The row
 * is in the overdue table because of the payload's flag — the words are what tell a person
 * WHY, and they name the real expected month rather than hiding it.</p>
 */
function RoleRow({ row }: { row: VacancyRow }): JSX.Element {
  return (
    <tr
      className={`vac-role${row.overdue ? " vac-role--overdue" : ""}`}
      data-job={row.jobId}
      data-month={row.estPlacedMonth}
    >
      <th scope="row">
        <span className="vac-role__title">{row.title}</span>
        {row.companyName && <span className="vac-role__company">{row.companyName}</span>}
        {row.overdue && (
          <span className="vac-flag">
            {OVERDUE_LABEL} — expected {monthLabel(row.estPlacedMonth)}
          </span>
        )}
      </th>
      <td className="vac-who">
        <span
          className="vac-swatch"
          aria-hidden="true"
          style={{ background: row.consultantColorVar }}
        />
        <span className="vac-who__name">{row.consultantName}</span>
      </td>
      <td className="vac-prio">
        {row.priority ? (
          <PriorityChip priority={row.priority} />
        ) : (
          /* Unclassified is an ordinary state (a vacancy nobody has priced yet), and it reads
             as the family's quiet dash rather than as a missing chip. */
          <span className="vac-prio__none is-zero">{NO_PRIORITY_MARK}</span>
        )}
      </td>
      <td className="num">{row.estValueLabel}</td>
    </tr>
  );
}

// ── The page ────────────────────────────────────────────────────────────────────────────

export function VacanciesPage(): JSX.Element {
  const identity = useClientTenant();
  const tenantId = identity.tenantId;

  // The tab says what the nav rail says, because both read the one label (#1163 AC-4).
  usePageTitle(VACANCIES_LABEL);

  const forecast = useForecastRoles(tenantId);
  const data = forecast.data;

  // The whole page model, from the selectors. Cheap enough to run per paint (one pass over a
  // few dozen roles) and NOT memoised on purpose: `data` is a new object identity on every
  // refetch anyway, so a memo here would buy nothing and hide where the numbers come from.
  const head = headline(data);
  const groups = monthGroups(data);
  const overdue = overdueRoles(data);
  const chart = stackedChartData(data);

  // "Still loading" is defined by the ANSWER, not by a fetch flag — the query is disabled
  // until /me yields a tenant, and a disabled query reports isLoading:false, which would flash
  // the empty state at a client whose vacancies are on their way.
  const rolesUnresolved = tenantId !== null && data === undefined && forecast.error === null;
  const isBusy = identity.isLoading || rolesUnresolved;
  const failed = identity.error ?? forecast.error ?? null;

  // An empty list is UNAMBIGUOUS on this payload: the role-level read is computed live and
  // fails loud (a provider or compute fault is a 5xx, never an empty 200), so `roles: []`
  // genuinely means "no open vacancies" and can be said in those words.
  const isEmpty = !!data && data.roles.length === 0;
  const asOf = formatAsOf(data?.as_of);

  const priced = head.priorityEnabled;
  const headlineValue = priced ? head.headlineLabel : head.forecastLabel;
  const headlineCaption = priced ? head.headlineCaption : ALL_VACANCIES_CAPTION;
  // The overdue slice of WHICHEVER headline the page shows: the A+R £ when priced, the
  // every-letter £ when not. Read from the headline's own sub-object, never re-derived here.
  const overdueSliceLabel = priced ? head.overdue.headlineValueLabel : head.overdue.valueLabel;

  return (
    <>
      <PageHeader
        title={VACANCIES_LABEL}
        subtitle="Your open vacancies and what they are expected to be worth, grouped by the month they are expected to be placed."
        actions={
          data && !isEmpty && asOf ? (
            <p className="page-header__meta">
              Data as at <span className="num">{asOf}</span>
            </p>
          ) : undefined
        }
      />

      {failed && (
        <div className="state state--error" role="status">
          <h2 className="state__title">We could not load this report</h2>
          <p className="state__body">
            Please try again in a moment. If it keeps happening, contact your AR Dept contact.
          </p>
        </div>
      )}

      {!failed && isBusy && (
        <div className="vac-loading" aria-busy="true" aria-live="polite">
          <span className="vac-loading__label">Loading your vacancies…</span>
          <div className="skeleton" style={{ width: "35%" }} />
          <div className="skeleton" style={{ width: "80%" }} />
          <div className="skeleton" />
          <div className="skeleton" />
        </div>
      )}

      {!failed && !isBusy && isEmpty && (
        <EmptyState
          iconName="list-checks"
          title="No open vacancies"
          body="As soon as there are vacancies on your books, they will appear here with the consultant looking after them and what they are expected to be worth."
        />
      )}

      {!failed && !isBusy && !isEmpty && data && (
        <>
          {/* ── The headline: A+R prominent, everything else shown and not counted ── */}
          <section className="vac-section" aria-labelledby="vac-headline-head">
            <h2 className="vac-section__head" id="vac-headline-head">
              Expected value
            </h2>
            <div className="vac-headline">
              <div className="vac-headline__main">
                <p className="vac-headline__k">{headlineCaption}</p>
                <p className="vac-headline__v">{headlineValue}</p>
                <p className="vac-headline__note">
                  {priced
                    ? "Priority A and R vacancies, across every month ahead and including any " +
                      "past their expected month. B and C vacancies are listed in full below " +
                      "and counted separately."
                    : "Every open vacancy, across every month ahead and including any past its " +
                      "expected month. No priorities are being maintained against your " +
                      "vacancies yet, so none is singled out here."}
                </p>
              </div>

              <dl className="vac-splits">
                {priced && (
                  <div className="vac-splits__item">
                    <dt>{head.inclBCaption}</dt>
                    <dd>{head.inclBLabel}</dd>
                  </div>
                )}
                {priced && (
                  <div className="vac-splits__item">
                    <dt>Every priority</dt>
                    <dd>{head.forecastLabel}</dd>
                  </div>
                )}
                {/* Overdue is a SLICE of the headline above, not a figure beside it (phase
                    578, card #2167): the headline counts every live vacancy, overdue
                    included, and this item says how much of it is overdue. It is the SAME
                    slice the Monday card's Overdue tile shows — the £ over the headline's
                    letters (A+R, or every letter when no priorities are kept), the count over
                    every letter — both read from `head.overdue`, never re-derived here. */}
                {overdue.roles.length > 0 && (
                  <div className="vac-splits__item">
                    <dt>{OVERDUE_LABEL}</dt>
                    <dd>{`${overdueSliceLabel} · ${vacancyCount(head.overdue.count)}`}</dd>
                  </div>
                )}
                <div className="vac-splits__item">
                  <dt>Vacancies</dt>
                  <dd>{head.roleCount}</dd>
                </div>
              </dl>
            </div>
          </section>

          {/* ── The monthly stack: months across, consultants stacked, roster colours ── */}
          {chart.series.length > 0 && (
            <section className="vac-section" aria-labelledby="vac-chart-head">
              <h2 className="vac-section__head" id="vac-chart-head">
                By month and consultant
              </h2>
              <p className="vac-section__note">
                Expected value of the vacancies in each month, split by the consultant looking
                after them. Overdue vacancies are not in this chart — they are listed on their
                own below.
              </p>
              <div className="vac-chart">
                <StackedBarChart
                  categories={chart.categories}
                  series={chart.series}
                  formatValue={formatGbp}
                />
              </div>
            </section>
          )}

          {/* ── Overdue: the partition that belongs to no month ── */}
          {overdue.roles.length > 0 && (
            <section className="vac-section vac-section--overdue" aria-labelledby="vac-overdue-head">
              <h2 className="vac-section__head" id="vac-overdue-head">
                {OVERDUE_LABEL}
              </h2>
              <p className="vac-section__note">
                Vacancies whose expected placement month has already passed. They are listed
                here, with the month they were expected in, rather than under a month still to
                come.
              </p>
              <div className="tbl-wrap">
                <table className="tbl vac-roles" aria-label="Overdue vacancies">
                  <RoleColumns />
                  <tbody>
                    {overdue.roles.map((row) => (
                      <RoleRow key={row.jobId} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── The month bands: soonest first, each band only where there are vacancies ── */}
          {groups.length > 0 && (
            <section className="vac-section" aria-labelledby="vac-months-head">
              <h2 className="vac-section__head" id="vac-months-head">
                By month
              </h2>
              <p className="vac-section__note">
                Every open vacancy, under the month it is expected to be placed in, soonest
                first. A month with nothing expected in it is simply not shown.
              </p>
              <div className="tbl-wrap">
                <table className="tbl vac-roles" aria-label="Vacancies by month">
                  <RoleColumns />
                  <tbody>
                    {groups.map((group) => (
                      <Fragment key={group.month}>
                        <tr className="vac-band" data-band={group.month}>
                          <th scope="colgroup" colSpan={ROLE_COLUMNS.length}>
                            <span className="vac-band__label">{group.label}</span>
                            <span className="vac-band__value num">{group.valueLabel}</span>
                          </th>
                        </tr>
                        {group.roles.map((row) => (
                          <RoleRow key={row.jobId} row={row} />
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}

export default VacanciesPage;
